import {useState, useEffect} from 'react'
import {getNbrePieceAcceptees} from '../../../../../components/requests'

interface StatusText {
  accepted: number | 'Chargement...'
  refused: number | 'Chargement...'
  annuler: number | 'Chargement...'
  accepteeBloquee: number | 'Chargement...'
  proposee: number | 'Chargement...'
  accepteeIndispo: number | 'Chargement...'
}

const PieceStatus = ({piece, demandeClient}) => {
  const [statusText, setStatusText] = useState<StatusText>({
    accepted: 'Chargement...',
    refused: 'Chargement...',
    annuler: 'Chargement...',
    accepteeBloquee: 'Chargement...',
    proposee: 'Chargement...',
    accepteeIndispo: 'Chargement...'
  })
  const [isProposed, setIsProposed] = useState<boolean>(false)
  const [isAnnuler, setIsAnnuler] = useState<boolean>(false)
  const [isEchec, setIsEchec] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const getDataStatusPieces = async (
    idDemandePiece: number
  ): Promise<{
    accepted: number
    refused: number
    annuler: number
    accepteeBloquee: number
    proposee: number
    accepteeIndispo: number
  }> => {
    try {
      const res = await getNbrePieceAcceptees(idDemandePiece)
      if (res && res.data) {
        let accepted = 0
        let refused = 0
        let annuler = 0
        let accepteeBloquee = 0
        let proposee = 0
        let accepteeIndispo = 0

        res.data.forEach(
          (item: {
            isAccepted: boolean
            isRefused: boolean
            dateAnnulation: string
            isAcceptedBlocked: number
            isProposed: boolean
            dateEchec: string
          }) => {
            if (item.isAccepted) accepted++
            if (item.isRefused) refused++
            if (item.dateAnnulation) annuler++
            if (item.isAcceptedBlocked) accepteeBloquee++
            if (item.dateEchec) accepteeIndispo++
            if (
              !item.isAccepted &&
              !item.isRefused &&
              !item.dateAnnulation &&
              !item.isAcceptedBlocked &&
              item.isProposed &&
              !item.dateEchec
            ) {
              proposee++
            }
          }
        )

        return {accepted, refused, annuler, accepteeBloquee, proposee, accepteeIndispo}
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des données :', error)
    }
    return {accepted: 0, refused: 0, annuler: 0, accepteeBloquee: 0, proposee: 0, accepteeIndispo: 0}
  }

  useEffect(() => {
    if (piece?.stock === 'chargement') return

    setStatusText({
      accepted: 'Chargement...',
      refused: 'Chargement...',
      annuler: 'Chargement...',
      accepteeBloquee: 'Chargement...',
      proposee: 'Chargement...',
      accepteeIndispo: 'Chargement...'
    })
    setIsLoading(true)

    const pieces = piece?.clientDemandePieceProposee ?? []
    // Cas initial : aucune pièce proposée du tout -> 0/0 proposee
    if (pieces.length === 0) {
      setStatusText({
        accepted: 0,
        refused: 0,
        annuler: 0,
        accepteeBloquee: 0,
        proposee: 0,
        accepteeIndispo: 0
      })
      setIsLoading(false)
      return
    }

    const annuler = piece?.clientDemandePieceProposee?.some((p) => p.dateAnnulation != null)
    const acceptIndispo = piece?.clientDemandePieceProposee?.some((p) => p.dateEchec != null)

    if (annuler) {
      setIsAnnuler(true)
    }

    if (acceptIndispo)
    {
      setIsEchec(true)
    }

    //piece proposee:
    const proposee = piece?.clientDemandePieceProposee?.some(
      (p) => !p.isAccepted && !p.isRefused && !p.isAcceptedBlocked
    )

    if (
      piece.demande.clientDemandeEtape.code === 'ACCEP' ||
      piece.demande.clientDemandeEtape.code === 'REFUS' ||
      piece.demande.clientDemandeEtape.code === 'BLOQU' ||
      isAnnuler ||
      isEchec ||
      proposee
    ) {
      setIsProposed(true)
      getDataStatusPieces(piece.id).then(
        ({accepted, refused, annuler, accepteeBloquee, proposee, accepteeIndispo}) => {
          setStatusText({
            accepted,
            refused,
            annuler,
            accepteeBloquee,
            proposee,
            accepteeIndispo,
          })
          setIsLoading(false)
        }
      )
    }
  }, [piece, demandeClient])
  // demandeClient pour re trigger le useEffect sur chaque changement sur demandeClient
  // Surtout les changements de statut

  return (
    <>
      {(() => {
        const total = piece.clientDemandePieceProposee?.length ?? 0

        if (statusText.proposee === 'Chargement...') return null

        // Des pièces existent mais plus aucune "encore proposée" -> badge masqué
        if (total > 0 && statusText.proposee === 0) return null

        return (
          <span className='badge bg-info text-white justify-content-center'>
            {statusText.proposee}/{total} Proposée(s)
          </span>
        )
      })()}

      {isProposed && (
        <>
          {isLoading && (
            <span className='badge bg-light text-gray-500 justify-content-center'>
              Chargement...
            </span>
          )}

          {statusText.accepted !== 'Chargement...' && statusText.accepted > 0 && (
            <span className='badge bg-success text-white justify-content-center'>
              {statusText.accepted}/{piece.clientDemandePieceProposee.length ?? 0} Acceptée(s)
            </span>
          )}

          {statusText.refused !== 'Chargement...' && statusText.refused > 0 && (
            <span className='badge bg-danger text-white justify-content-center'>
              {statusText.refused}/{piece.clientDemandePieceProposee.length ?? 0} Refusée(s)
            </span>
          )}

          {statusText.annuler !== 'Chargement...' && statusText.annuler > 0 && (
            <span className='badge bg-light justify-content-center text-muted'>
              {statusText.annuler}/{piece.clientDemandePieceProposee.length ?? 0} Annulée(s)
            </span>
          )}

          {statusText.accepteeBloquee !== 'Chargement...' && statusText.accepteeBloquee > 0 && (
            <span className='badge badge-light-warning justify-content-center'>
              {statusText.accepteeBloquee}/{piece.clientDemandePieceProposee.length ?? 0}{' '}
              Acceptée(s) bloquée(s)
            </span>
          )}

          {statusText.accepteeIndispo !== 'Chargement...' && statusText.accepteeIndispo > 0 && (
            <span className='badge badge-light-warning-echec justify-content-center'>
              {statusText.accepteeIndispo}/{piece.clientDemandePieceProposee.length ?? 0}{' '}
              Acceptée(s) Indisponible
            </span>
          )}
        </>
      )}
      {piece.clientDemandePieceProposee?.length > 0 &&
        (() => {
          const total = piece.clientDemandePieceProposee.length
          const aProposer = piece.clientDemandePieceProposee.filter((p) => !p.isProposed).length
          if (aProposer === 0) return null
          return (
            <span className='badge badge-secondary justify-content-center'>
              {aProposer}/{total} À proposer
            </span>
          )
        })()}
    </>
  )
}

export default PieceStatus
